'use client'

import React, { createContext, useContext, useMemo, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/api'
import { useAuth } from './auth-context'
import { useOrg } from './org-context'

type Features = {
  career_pages: boolean
  career_pages_custom_domain: boolean
  opendb_search: boolean
  resdb_access: boolean
  resdb_ai_recommendations: boolean
  resdb_realtime_syncing: boolean
  talent_scout: boolean
  job_create: boolean
}

type SubscriptionData = {
  org_id: string
  user_id: string
  billing_type: 'admin-managed' | 'self-managed' | string
  plan: {
    plan_id: string
    title: string
    is_trial: boolean
    type: string
    current_term_end: Date
    status: string
    licenses: number
    team_size: number
    trial_used: boolean
  } | null
  features: Features
  active: boolean
  status: 'active' | 'inactive' | 'cancelled' | string
}

type ApiResponse = {
  success: boolean
  data: SubscriptionData
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null
  refetch: () => void
  isLoading: boolean
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null)

interface SubscriptionProviderProps {
  children: React.ReactNode
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user, loading: authLoading, bootstrapData } = useAuth()
  const { currentOrg } = useOrg()
  const queryClient = useQueryClient()

  const userId = user?.user_id ?? null
  const orgId = currentOrg?.id ?? null

  const shouldEnable = !authLoading && !!userId && !!orgId

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subscription', userId, orgId],
    queryFn: async () => {
      const res = await api.get<ApiResponse>('/recruiter/subscriptions/info')
      return res.data?.data
    },
    enabled: shouldEnable,
    initialData: () => bootstrapData?.subscription || undefined,
    initialDataUpdatedAt: bootstrapData ? Date.now() : undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes stale since it is loaded in bootstrap
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  })

  const subscriptionData = bootstrapData?.subscription

  useEffect(() => {
    if (subscriptionData && userId && orgId) {
      queryClient.setQueryData(['subscription', userId, orgId], subscriptionData)
    }
  }, [subscriptionData, userId, orgId, queryClient])

  const value = useMemo(
    () => ({
      subscription: data || null,
      refetch,
      isLoading: isLoading && !!orgId && !bootstrapData,
    }),
    [data, isLoading, refetch, orgId, bootstrapData]
  )

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider')
  }
  return context
}

export const useSubscriptionFeatures = (feature: keyof Features) => {
  const { subscription } = useSubscription()
  return subscription?.features[feature] || false
}

export const useSubscriptionStatus = () => {
  const { subscription } = useSubscription()
  return {
    isSubscriptionActive: subscription?.active || false,
    plan: subscription?.plan,
  }
}
